"use client";

import { useState, useEffect, type ReactNode } from "react";
import AddModel from "../../components/Add/AddModel";
import AddBottomButton from "../../components/Add/AddBottomButton";
import { useDispatch } from "react-redux";
import { resetManualState } from "../../store/slices/addManualSlice";
import { showToast } from "../../components/toastMessage/toastSlice";
import useApi from "../../services/useApi";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { inputFieldContent } from "../../components/inputfields/InputFieldContent";
import useTranslation from "../../components/CustomHooks/useTranslation";
import AddInputs from "../../components/Add/AddInputs";
import TextArea from "../../components/inputfields/TextArea";
import CustomModal from "../../components/CustomModal";
import PinVerificationModel from "../../components/Models/PinVerificationModel";
import endpoints from "../../services/endpoints";
import TransactionReceipt from "./TransactionReceipt";
import { fetchAuthSession } from "@aws-amplify/auth";
import Paid from "./Paid";
import PaidMobileMoney from "./PaidMobileMoney";
import CustomRadioButton from "../../components/buttons/CustomRadioButton";
import NestedDropdown from "../../components/Dropdowns/NestedDropdown";
import InputLabel from "../../components/inputfields/InputLabel";
import InputField from "../../components/inputfields/InputField";
import usePrivilege from "../../components/CustomHooks/usePrivilege";
import TertiaryButton from "../../components/buttons/TertiaryButton";
import Image from "../../components/images/Image";
import ErrorMessage from "../../components/ErrorMessages/ErrorMessage";

/**
 * Props interface for the BaseTopUpRequest component
 * @property {ReactNode} children - Child components to render within the form
 * @property {boolean} showWalletSelection - Whether to show wallet selection options
 * @property {boolean} showPaymentTypeSelection - Whether to show payment type selection options
 * @property {ReactNode} headerContent - Custom header content to display above the form
 * @property {string} defaultPaymentType - Default payment type ("advance" or "paid")
 * @property {string} defaultWallet - Default wallet type
 */
type BaseTopUpRequestProps = {
  children?: ReactNode;
  showWalletSelection?: boolean;
  showPaymentTypeSelection?: boolean;
  headerContent?: ReactNode;
  defaultPaymentType?: "advance" | "paid" | "stock_transfer";
  defaultWallet?: string;
  transactionData?: any;
  useNestedDropdown?: boolean;
};

/**
 * BaseTopUpRequest - A reusable component for E-Top Up request functionality
 *
 * This component handles the common functionality for creating E-Top Up requests,
 * including form state management, validation, API interactions, and UI rendering.
 * It can be configured through props to show/hide certain UI elements and set default values.
 */
export default function BaseTopUpRequest({
  children,
  showWalletSelection = true,
  showPaymentTypeSelection = true,
  headerContent,
  defaultPaymentType = "advance",
  defaultWallet = "E Top Up",
  transactionData,
  useNestedDropdown = false,
}: BaseTopUpRequestProps) {
  // Redux and router hooks
  const dispatch = useDispatch();
  const location = useLocation();

  // Custom hooks for translation and API
  const { notEnglish, translate } = useTranslation(inputFieldContent);
  const { id } = useParams();
  const { PostAPI, getAPI } = useApi();
  const navigate = useNavigate();
  const isSuperAdmin = usePrivilege('superadmin');

  // API endpoints
  const {
    raiseETopUpRequest,
    raiseETopUpRequestPaid,
    repayAdvanceETopUpRequest,
  } = endpoints;
  const walletTransferBulkAPI = "merchant-recharge/wallet/transfer";

  // State for PIN verification
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [pin, setPin] = useState<string[]>(new Array(4).fill(""));

  // State for USSD code verification (Stock Transfer)
  const [ussdCodeInput, setUssdCodeInput] = useState("");
  const [ussdVerifyLoading, setUssdVerifyLoading] = useState(false);
  const [ussdVerified, setUssdVerified] = useState(false);

  // Initial form data with default values from props
  const initialFormData = {
    payment_type: defaultPaymentType,
    choose_wallet: defaultWallet,
    amount: "",
    request_from: {},
    pin_interval: "",
    isDontAskAgainChecked: false,
    receiver_payment_source: "Bank",
    receiver_bank: {},
    sender_payment_source: "Bank",
    sender_bank: {},
    transaction_receipt: "",
    choose_platform: "",
    hesabpay_account_number: "",
    associated_phone_number: "",
  };

  // State for UI loading indicators
  const [popUpLoading, setPopUpLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  // Form and validation state
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [text, setText] = useState<string>("");
  const [textError, setTextError] = useState<string>("");

  // Modal and view state
  const [responseData, setResponseData] = useState({});
  const [isTransactionReceipt, setIsTransactionReceipt] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isVerificationModelOpen, setIsVerificationModelOpen] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isPopupPage, setIsOpenPage] = useState(1);

  /**
   * Handles input changes for form fields with validation
   * @param {string} key - The field name to update
   * @param {string|boolean|any[]} value - The new value for the field
   */
  const handleInputChange = (key: string, value: string | boolean | any[]) => {
    // Validate amount field to ensure it's a valid number with up to 2 decimal places
    if (key === "amount" && typeof value === "string") {
      const regex = /^\d*\.?\d{0,2}$/;
      if (!regex.test(value)) return;
      // Only allow numbers and at most one decimal point
      if (value === '' || /^\d*\.?\d*$/.test(value)) {
        // Validate decimal places (max 2)
        const parts = value.split('.');
        if (parts.length > 1 && parts[1].length > 2) {
          return; // Prevent input if more than 2 decimal places
        }

        // Validate max length (max 15 characters)
        if (value.length > 15) {
          return; // Prevent input if longer than 15 characters
        }

        // Validate integer part (max 12 digits)
        if (parts[0].length > 12) {
          return; // Prevent input if more than 12 digits before decimal
        }
      }
    }

    // Validate phone number format for associated_phone_number field
    if (
      key === "associated_phone_number" &&
      typeof value === "string" &&
      value.length > 0
    ) {
      // This regex ensures:
      // 1. "+" symbol only allowed at the beginning
      // 2. No consecutive spaces or hyphens
      // 3. Only numbers, spaces, and hyphens allowed after optional leading "+"
      const regex = /^\+?(?!.*[ -][ -])(?!.*[+].*[+])[0-9 -]*$/;
      if (!regex.test(value)) return;
    }

    // Remove spaces from hesabpay account number
    if (key === "hesabpay_account_number") {
      value = value.replace(/\s/g, ""); // Remove spaces
    }

    // Reset form when payment type changes
    if (key === "payment_type") {
      setText("");
      setFormData(initialFormData);
      setErrors({});
    }

    // Handle special case: when receiver payment source is Cash, set sender payment source to Cash too
    if (key === "receiver_payment_source" && value === "Cash") {
      setFormData((prevData) => ({
        ...prevData,
        ["sender_payment_source"]: value,
      }));
    }

    // Reset bank fields when receiver payment source changes
    if (key === "receiver_payment_source") {
      setFormData((prevData) => ({
        ...prevData,
        ["receiver_bank"]: {},
        ["sender_bank"]: {},
        ["transaction_receipt"]: "",
      }));
    }

    // Reset sender bank when sender payment source changes
    if (key === "sender_payment_source") {
      setFormData((prevData) => ({
        ...prevData,
        ["sender_bank"]: {},
      }));
    }

    // When receiver payment source is Bank, set sender payment source to Bank too
    if (key === "receiver_payment_source" && value === "Bank") {
      setFormData((prevData) => ({
        ...prevData,
        ["sender_payment_source"]: value,
      }));
    }

    // Update form data with new value
    setFormData((prevData) => ({
      ...prevData,
      [key]: value,
    }));

    // Clear error for the updated field
    setErrors((prevErrors) => ({
      ...prevErrors,
      [key]: "",
    }));

    // Reset related fields when platform changes
    if (key === "choose_platform") {
      setFormData((prevData) => ({
        ...prevData,
        hesabpay_account_number: "",
        associated_phone_number: "",
        transaction_receipt: "",
      }));
      setErrors((prevErrors) => ({
        ...prevErrors,
        hesabpay_account_number: "",
        associated_phone_number: "",
        transaction_receipt: "",
      }));
    }

    // Reset all payment-related fields when receiver payment source changes
    if (key === "receiver_payment_source") {
      setFormData((prevData) => ({
        ...prevData,
        receiver_bank: {},
        sender_bank: {},
        transaction_receipt: "",
        choose_platform: "",
        hesabpay_account_number: "",
        associated_phone_number: "",
      }));
      setErrors((prevErrors) => ({
        ...prevErrors,
        receiver_bank: "",
        sender_bank: "",
        transaction_receipt: "",
        choose_platform: "",
        hesabpay_account_number: "",
        associated_phone_number: "",
      }));
    }
  };

  /**
   * Cleanup effect to reset manual state when component unmounts or route changes
   */
  useEffect(() => {
    return () => {
      dispatch(resetManualState());
    };
  }, [location.pathname, dispatch]);

  /**
   * Validates form data before submission
   * @returns {boolean} True if validation passes, false otherwise
   */
  const handleValidation = () => {
    const newErrors: any = {};

    // Validate amount field
    if (formData?.amount === "") {
      newErrors.amount = "This field is required";
    } else if (Number(formData?.amount) < 1) {
      newErrors.amount = "Amount must be at least AFA 1.00";
    } else if(headerContent && Number(formData?.amount) > Number(transactionData.payable_amount)) {
      newErrors.amount = "Amount must be less than or equal to the payable amount";
    }
    
    // Validate request_from field
    if (!headerContent && Object.keys(formData?.request_from).length === 0) {
      newErrors.request_from =
        useNestedDropdown && ussdCodeInput.trim() !== ""
          ? "Please verify the USSD code before submitting"
          : "This field is required";
    }

    // Additional validation for paid payment type
    if (formData?.payment_type === "paid") {
      if (formData?.receiver_payment_source === "Mobile Money") {
        // Validate mobile money fields
        if (formData?.choose_platform === "") {
          newErrors.choose_platform = "This field is required";
        }
        if (
          formData?.choose_platform === "Hesab Pay" &&
          formData?.hesabpay_account_number === ""
        ) {
          newErrors.hesabpay_account_number = "This field is required";
        }
        if (formData?.choose_platform !== "Hesab Pay") {
          if (formData?.associated_phone_number === "") {
            newErrors.associated_phone_number = "This field is required";
          } else if (formData.associated_phone_number.length < 10) {
            newErrors.associated_phone_number =
              "Invalid associated phone number";
          }
        }
        if (formData?.transaction_receipt === "") {
          newErrors.transaction_receipt = "This field is required";
        }
      } else {
        // Validate bank fields
        if (Object.keys(formData?.receiver_bank).length === 0) {
          newErrors.receiver_bank = "This field is required";
        }
        if (Object.keys(formData?.sender_bank).length === 0) {
          newErrors.sender_bank = "This field is required";
        }
        if (formData?.transaction_receipt === "") {
          newErrors.transaction_receipt = "This field is required";
        }
      }
    }

    // Update errors state and return validation result
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handles form submission
   * Validates form data and shows confirmation popup if valid
   */
  const handleSubmit = async () => {
    setLoading(true);
    setPin(new Array(4).fill(""));
    setErrorMessage("");
    setPopUpLoading(false);
    setFormData((prevData) => ({
      ...prevData,
      ["isDontAskAgainChecked"]: false,
    }));

    // Validate form data
    if (!handleValidation()) {
      setLoading(false);
      return;
    } else {
      // Show confirmation popup
      setIsPopupOpen(true);
      setLoading(false);
      setIsOpenPage(1);
    }
  };

  /**
   * Handles submission from the confirmation popup
   * Manages multi-step confirmation process and PIN verification
   */
  const handleSubmitPopup = async () => {
    setPopUpLoading(true);

    // For stock transfer, skip the two-step confirmation
    if (defaultPaymentType === "stock_transfer") {
      // Check if PIN verification is needed based on expiry timestamp
      const user = await fetchAuthSession({ forceRefresh: true });
      console
      const timestamp =
        user?.tokens?.idToken?.payload["custom:pin_expiry_timestamp"];
      if (timestamp) {
        const inputDate = new Date(timestamp);
        const currentDate = new Date();
        if (inputDate < currentDate) {
          // PIN expired, show verification modal
          setPopUpLoading(false);
          setIsVerificationModelOpen(true);
          setIsPopupOpen(false);
        } else {
          // PIN still valid, proceed without verification
          handleClickVerify(false);
        }
      } else {
        // No timestamp, show verification modal
        setPopUpLoading(false);
        setIsVerificationModelOpen(true);
        setIsPopupOpen(false);
      }
      return;
    }

    // Handle first page of confirmation popup for non-stock transfer
    if (isPopupPage === 1) {
      setIsOpenPage(2);
      setPopUpLoading(false);
      return;
    } else {
      // Check if PIN verification is needed based on expiry timestamp
      const user = await fetchAuthSession({ forceRefresh: true });
      const timestamp =
        user?.tokens?.idToken?.payload["custom:pin_expiry_timestamp"];
      if (timestamp) {
        const inputDate = new Date(timestamp);
        const currentDate = new Date();
        if (inputDate < currentDate) {
          // PIN expired, show verification modal
          setIsVerificationModelOpen(true);
          setIsPopupOpen(false);
        } else {
          // PIN still valid, proceed without verification
          handleClickVerify(false);
        }
      } else {
        // No timestamp, show verification modal
        setPopUpLoading(false);
        setIsVerificationModelOpen(true);
        setIsPopupOpen(false);
      }
    }
  };

  /**
   * Formats user data for display in dropdown
   * @param {Record<string, any>} value - User data object
   * @returns {string} Formatted string representation of user
   */
  const handleFormatValue = (value: Record<string, any>) => {
    if (Object.keys(value).length !== 0) {
      return `${value?.user_code ?? ""} | ${value?.first_name ?? ""} ${
        value?.last_name ?? ""
      }`;
    }
    return "";
  };

  /**
   * Configuration for form input fields
   */
  const inputs = {
    "": {
      Amount: {
        required: true,
        disabled: false,
        id: "amount",
        type: "input",
        tabIndex: 0,
        bottom: "mb-0",
      },
      ...(headerContent
        ? {}
        : useNestedDropdown
        ? {
            "Send To": {
              id: "send-to",
              type: "nested_dropDown",
              required: true,
              disabled: false,
              tabIndex: 2,
              bottom: "mb-0",
              placeholder: "Select",
              testId: "send-to",
            },
          }
        : {
            "Request From": {
              id: "request_from",
              type: "dynamic_dropDown",
              required: true,
              disabled: false,
              handelDynamicValue: handleFormatValue,
              tabIndex: 2,
              bottom: "mb-0",
              apiEndPoint: `admins/?type=super_admin&page_number=`,
            },
          }),
    },
  };

  const fetchUserData = async (endpoint: string, searchTerm: string, page: number = 1) => {
    try {
      const adminParam = endpoint === 'admin' ? '&type=admin' : '&assign_fee_mode=true';
      const response = await getAPI(`${endpoint}s/?page_number=${page}${adminParam}${searchTerm ? `&search=${encodeURIComponent(searchTerm)}` : ""}`);
      if (!response.error) {
        return {
          data: {
            users: response.data.data.map((user: any) => ({
              id: user.id,
              user_name: user.first_name + ' ' + (user.last_name ? user.last_name : ''),
              user_code: user.user_code,
              status: user.status || "Active"
            })),
            total_count: response.data.total_count
          }
        };
      }
      return { data: { users: [], total_count: 0 } };
    } catch (error) {
      console.error("Error fetching user data:", error);
      return {
        data: {
          users: [],
          total_count: 0,
        },
      };
    }
  };

  const baseDropdownOptions = [
    {
      label: "Master Distributor",
      value: "master_distributor",
      isNested: true,
      apiEndpoint: "master-distributor",
      id: "master-distributor",
      testId: "master-distributor",
    },
    {
      label: "Distributor",
      value: "distributor",
      isNested: true,
      apiEndpoint: "distributor",
      id: "distributor",
      testId: "distributor",
    },
    {
      label: "Sub Distributor",
      value: "sub_distributor",
      isNested: true,
      apiEndpoint: "sub-distributor",
      id: "sub-distributor",
      testId: "sub-distributor",
    },
    {
      label: "Agent",
      value: "agent",
      isNested: true,
      apiEndpoint: "agent",
      id: "agent",
      testId: "agent",
    },
    {
      label: "Retailer",
      value: "retailer",
      isNested: true,
      apiEndpoint: "retailer",
      id: "retailer",
      testId: "retailer",
    },
    {
      label: "Customer",
      value: "customers",
      isNested: true,
      apiEndpoint: "customer",
      id: "customer",
      testId: "customer",
    },
  ];

  const nestedDropdownOptions = isSuperAdmin 
    ? [{ label: "Admin", value: "admin", isNested: true, apiEndpoint: "admin", id: "admin", testId: "admin" }, ...baseDropdownOptions]
    : baseDropdownOptions;

  /**
   * Handles typing in the USSD code field
   * Any edit invalidates a previously verified user and clears the auto-populated fields
   */
  const handleUssdCodeInputChange = (value: string) => {
    const digitsOnly = value.replace(/\D/g, "");
    setUssdCodeInput(digitsOnly);
    setErrors((prevErrors) => ({
      ...prevErrors,
      request_from: "",
    }));
    if (ussdVerified) {
      setUssdVerified(false);
      setFormData((prevData) => ({
        ...prevData,
        request_from: {},
      }));
    }
  };

  /**
   * Verifies the entered USSD code against reports/users and, on an exact match,
   * auto-populates the read-only User Name and Code fields
   */
  const handleVerifyUssdCode = async () => {
    const code = ussdCodeInput.trim();
    if (!code) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        request_from: "This field is required",
      }));
      return;
    }

    setUssdVerifyLoading(true);
    setErrors((prevErrors) => ({
      ...prevErrors,
      request_from: "",
    }));

    try {
      const response = await getAPI(`reports/users?page_number=1&ussd_code=${encodeURIComponent(code)}`);
      const matchedUser = !response.error
        ? (response.data?.data || []).find((user: any) => user.ussd_code === code)
        : null;

      if (matchedUser) {
        setFormData((prevData) => ({
          ...prevData,
          request_from: {
            id: matchedUser.id,
            user_name: matchedUser.first_name + ' ' + (matchedUser.last_name ? matchedUser.last_name : ''),
            user_code: matchedUser.user_code,
            ussd_code: matchedUser.ussd_code,
            status: matchedUser.status || "Active",
          },
        }));
        setUssdVerified(true);
      } else {
        setFormData((prevData) => ({ ...prevData, request_from: {} }));
        setUssdVerified(false);
        setErrors((prevErrors) => ({
          ...prevErrors,
          request_from: "No user found for this USSD code",
        }));
      }
    } catch (error) {
      console.error("Error verifying USSD code:", error);
      setFormData((prevData) => ({ ...prevData, request_from: {} }));
      setUssdVerified(false);
      setErrors((prevErrors) => ({
        ...prevErrors,
        request_from: "Something went wrong. Please try again.",
      }));
    } finally {
      setUssdVerifyLoading(false);
    }
  };

  /**
   * Handles PIN verification and form submission to API
   * @param {boolean} isPin - Whether PIN verification is being used
   */
  const handleClickVerify = async (isPin: boolean) => {
    setVerifyLoading(true);
    setErrorMessage("");
    try {
      // Validate PIN if required
      if (isPin) {
        if (pin.includes("")) {
          setErrorMessage("This field is required");
          return;
        }
        if (formData.isDontAskAgainChecked && formData.pin_interval === "") {
          setErrors((prevErrors) => ({
            ...prevErrors,
            pin_interval: "This field is required",
          }));
          return;
        }
      }

      // Determine which API endpoint to use
      let endpoint = raiseETopUpRequest;

      // Prepare payload for API request
      let payload: { [key: string]: any } = {};
      
      // Handle stock transfer separately with wallet transfer bulk API
      if (defaultPaymentType === "stock_transfer") {
        endpoint = walletTransferBulkAPI;
        payload = {
          stock_transfer: true,
          pin: isPin ? pin.join("") : "",
          pin_interval: isPin ? (formData?.pin_interval || "") : "",
          records: [
            {
              user_code: formData?.request_from?.user_code,
              wallet_type: "etopup",
              amount: Number(formData?.amount),
              note: text,
            },
          ],
        };
      } else {
        // Standard payload for non-stock transfer
        payload = {
          amount: Number(formData?.amount),
          note: text,
          pin: pin.join(""),
          pin_interval: formData?.pin_interval,
        };
        if (!headerContent) {
          payload['wallet'] = "etopup";
          payload['payment_type'] = formData?.payment_type;
          payload["request_from"] = formData?.request_from?.id;
        }
      }

      // Add additional fields for paid payment type
      if (formData?.payment_type === "paid") {
        if (formData?.receiver_payment_source === "Mobile Money") {
          // Mobile money specific fields
          payload["sender_source_id"] =
            formData?.choose_platform === "Hesab Pay"
              ? formData?.hesabpay_account_number
              : formData?.associated_phone_number;
          payload["e_money_platform"] = formData?.choose_platform;
          payload["sender_payment_source"] = "Mobile Money";
          payload["receiver_payment_source"] = "Mobile Money";
        } else {
          // Bank specific fields
          payload["sender_source_id"] = formData?.sender_bank?.id;
          payload["sender_payment_source"] = formData?.sender_payment_source;
          payload["receiver_payment_source"] =
            formData?.receiver_payment_source;
          payload["receiver_source_id"] = formData?.receiver_bank?.id;
        }
        payload["transaction_receipt"] = formData?.transaction_receipt;
        endpoint = raiseETopUpRequestPaid;
      }
      if (headerContent) {
        endpoint = `${repayAdvanceETopUpRequest}/${id}`;
      }

      // Send API request
      const response = await PostAPI(endpoint, payload);
      if (response.error) {
        // Handle API error
        const errorMsg = response?.data?.response?.data?.message || "Something went wrong. Please try again.";
        
        // Check if it's a PIN-related error
        const isPinError = errorMsg.toLowerCase().includes('pin') || errorMsg.toLowerCase().includes('invalid');
        
        if (isPin && isPinError) {
          // For PIN errors, keep modal open and show error in modal
          setErrorMessage(errorMsg);
          // Don't close modal or show toast for PIN errors
        } else {
          // For other errors, show toast and close modals
          setErrorMessage("");
          dispatch(showToast({ type: "error", message: errorMsg }));
          setIsPopupOpen(false);
          setIsVerificationModelOpen(false);
        }
      } else {
        // Handle successful response
        if (defaultPaymentType === "stock_transfer") {
          // Format stock transfer response for TransactionReceipt
          const apiData = response?.data?.data || response?.data;
          const transactionDetails = apiData?.transaction_details || {};
          console.log('transactionDetails', transactionDetails)
          // Extract amount number from "AFA 100.00" format
          const amountStr = transactionDetails?.amount || "";
          
          setResponseData({
            date: transactionDetails?.date || "",
            transaction_id: transactionDetails?.transaction_id || "",
            type_of_transaction: transactionDetails?.type_of_transaction || "Stock Transfer",
            amount: amountStr,
            sender: transactionDetails?.sender || "-",
            receiver: transactionDetails?.receiver || "-",
          });
        } else {
          setResponseData(response?.data?.data);
        }
        // Reset form immediately after successful response
        setFormData(initialFormData);
        setText("");
        setErrors({});
        setUssdCodeInput("");
        setUssdVerified(false);
        setIsPopupOpen(false);
        setIsTransactionReceipt(true);
        setIsVerificationModelOpen(false);
      }
    } catch (error) {
      // Handle unexpected errors
      console.error(error);
      const errorMsg = "Something went wrong. Please try again.";
      
      // If PIN verification was attempted, show error in modal
      if (isPin) {
        setErrorMessage(errorMsg);
        // Keep modal open for PIN errors
      } else {
        setErrorMessage("");
        dispatch(showToast({ type: "error", message: errorMsg }));
        setIsPopupOpen(false);
        setIsVerificationModelOpen(false);
      }
    } finally {
      // Reset loading states
      setVerifyLoading(false);
      setPopUpLoading(false);
    }
  };

  /**
   * Handles completion of transaction receipt view
   * Resets form and state for a new transaction
   */
  const handleClickDone = () => {
    if(headerContent) {
      navigate(`/e-top-up-request/e-top-up/raise-e-top-up-request/pay`);
    } else {
      setResponseData({});
      setIsTransactionReceipt(false);
      setIsPopupOpen(false);
    }
  };

  return (
    <div>
      {/* Render custom header content if provided */}
      {headerContent}

      {/* Main form container */}
      <AddModel ModelHight={headerContent ? "h-addwithViewHight" : ""}>
        {/* Render children components */}
        {children}

        {/* Payment type selection */}
        {showPaymentTypeSelection && (
          <div
            className={`w-1/7 mt-8 ${
              formData.payment_type === "paid"
                ? `${notEnglish ? "pl-3" : " pr-3"} border-[#E5E9EB]`
                : ""
            }`}
          >
            <CustomRadioButton
              options={[
                { id: "advance", label: "Advance" },
                { id: "paid", label: "Paid" },
              ]}
              selected={formData?.payment_type}
              onChange={(value) => handleInputChange("payment_type", value)}
              padding={"px-0"}
              dir={notEnglish ? "rtl" : "ltr"}
            />
          </div>
        )}

        {/* Wallet selection */}
        {showWalletSelection && (
          <div
            className={`w-1/7 ${`${
              notEnglish ? " pl-3" : "pr-3"
            } border-[#E5E9EB]`}`}
          >
            <h1
              className={`text-heading3 text-primary_text pb-4 ${defaultPaymentType === "stock_transfer" ? "pt-3" : "pt-4"}`}
            >
              {translate("Choose Wallet")}
            </h1>
            <CustomRadioButton
              options={[
                { id: "E Top Up", label: "E Top Up", testID: "e-top-up" },
                { id: "Digital Payment", label: "Digital Payment", testID: "digital-payment" },
              ]}
              isDisabled={["Digital Payment"]}
              selected={formData?.choose_wallet}
              onChange={(value) => handleInputChange("choose_wallet", value)}
              padding={"px-0"}
              dir={notEnglish ? "rtl" : "ltr"}
            />
          </div>
        )}

        {/* Main form inputs */}
        <div
          className={`flex flex-col mt-4 ${
            headerContent && !errors.amount ? "mb-5" : ""
          }`}
          dir={notEnglish ? "rtl" : "ltr"}
        >
          {useNestedDropdown ? (
            <>
              {/* Send To (USSD Code) + auto-populated User Name and Code + Amount */}
              <div className="flex flex-col sm:flex-row gap-x-14">
                <div className="w-full sm:w-1/3">
                  <InputLabel labelName="Send To (USSD Code)" id="ussd-code" required={true} />
                  <div className="relative">
                    <input
                      id="ussd-code"
                      data-testid="send-to"
                      className={`w-full h-14 py-5 ${
                        ussdVerified
                          ? "pr-[50px]"
                          : ussdVerifyLoading
                          ? "pr-[54px]"
                          : ussdCodeInput.length !== 0
                          ? "pr-[70px]"
                          : "pr-[15px]"
                      } pl-[15px] focus:pl-[14px] border rounded-lg text-subTitle outline-none focus:border-2 ${
                        errors?.request_from ? "border-negative" : "border-P_light_active"
                      }`}
                      placeholder={translate("Enter USSD code")}
                      autoComplete="off"
                      inputMode="numeric"
                      value={ussdCodeInput}
                      onChange={(event) => handleUssdCodeInputChange(event.target.value)}
                    />
                    <span
                      className={`absolute ${
                        ussdVerified || ussdVerifyLoading ? "right-[15px]" : "right-[5px]"
                      } ${
                        ussdVerified ? "top-[19px]" : ussdVerifyLoading ? "top-[15px]" : "top-2"
                      }`}
                    >
                      {ussdVerified ? (
                        <Image src="success-tick" className="h-5 w-5" />
                      ) : (
                        ussdCodeInput.length !== 0 && (
                          <TertiaryButton
                            testId="send-to_verify"
                            buttonName={translate("Verify")}
                            id="Verify"
                            handleClick={handleVerifyUssdCode}
                            loading={ussdVerifyLoading}
                          />
                        )
                      )}
                    </span>
                  </div>
                  {errors?.request_from && (
                    <ErrorMessage errorMessage={errors.request_from} testId="error-send-to" />
                  )}
                </div>
                <div className="w-full sm:w-1/3">
                  <InputLabel labelName="User Name and Code" id="user-name-and-code" />
                  <div
                    data-testid="user-name-and-code"
                    className="flex items-center py-3 px-4 w-full h-14 text-subTitle bg-disable_fill rounded-lg border border-disable_stroke text-gray-400 cursor-not-allowed"
                  >
                    <span className="truncate">
                      {formData?.request_from?.user_code
                        ? `${formData?.request_from?.user_name?.trim()} (${formData?.request_from?.user_code})`
                        : "User Name and Code"}
                    </span>
                  </div>
                </div>
                <div className="w-full sm:w-1/3">
                  <InputField
                    inputItem={{ labelName: "Amount", id: "amount" }}
                    required={true}
                    tabIndex={0}
                    value={formData?.amount || ""}
                    errorMessage={errors?.amount || ""}
                    onChange={(event) => handleInputChange("amount", event.target.value)}
                    customWidth={true}
                    testId="amount"
                  />
                </div>
              </div>
              <p className="mt-4 p-2 rounded-lg bg-information_light text-body1 text-primary_text">
                {translate(
                  "Note: The User Name and User Code fields are automatically fetched when USSD is entered. These fields are auto populated and cannot be edited."
                )}
              </p>
            </>
          ) : (
            <AddInputs
              mapping={true}
              inputs={inputs}
              totalInputCount={7}
              onInputChange={handleInputChange}
              inputValues={formData}
              errors={errors}
              isDisabledField={id ? ["phone_number"] : []}
              edit={id ? true : false}
              RequiredPostalCode={true}
            />
          )}
        </div>

        {/* Conditional rendering of payment method components */}
        {formData.payment_type === "paid" &&
          formData.receiver_payment_source !== "Mobile Money" && (
            <Paid
              formData={formData}
              handleInputChange={handleInputChange}
              notEnglish={notEnglish}
              errors={errors}
            />
          )}
        {formData.payment_type === "paid" &&
          formData.receiver_payment_source === "Mobile Money" && (
            <PaidMobileMoney
              formData={formData}
              handleInputChange={handleInputChange}
              notEnglish={notEnglish}
              errors={errors}
            />
          )}

        {/* Note text area */}
        <div dir={notEnglish ? "rtl" : "ltr"} className="pt-4">
          <TextArea
            top={"pt-0"}
            required={false}
            label={"Note"}
            text={text}
            setText={setText}
            textError={textError}
            setTextError={setTextError}
            maxLength={250}
            placeholderRequied={false}
            id="note"
          />
        </div>
      </AddModel>

      {/* Submit button */}
      <AddBottomButton
        buttonWidth={"w-[140px]"}
        buttonName={`Submit`}
        buttonClass={`w-[140px] whitespace-nowrap cursor-pointer`}
        align="end"
        onClick={handleSubmit}
        disabled={false}
        loading={loading}
        testID={useNestedDropdown ? 'submit-stock-transfer' : 'submit-e-top-up-request'}
      />

      {/* Confirmation popup */}
      {isPopupOpen && (
        <CustomModal
          isOpen={isPopupOpen}
          onClose={() => setIsPopupOpen(false)}
          title={translate(defaultPaymentType === "stock_transfer" ? "Stock Transfer" : "E Top Up Request")}
          subText={
            defaultPaymentType === "stock_transfer"
              ? translate(
                  `You are about to initiate a Stock Transfer to ${formData?.request_from?.user_code} - ${formData?.request_from?.user_name || formData?.request_from?.name || `${formData?.request_from?.first_name ?? ""} ${formData?.request_from?.last_name ?? ""}`.trim()} for AFA ${Number(formData?.amount).toFixed(2)}.`
                )
              : isPopupPage === 1
              ? translate(
                  `${headerContent && defaultPaymentType === "paid" 
                    ? `You are about to repay the advance taken from ${transactionData?.requested_user_code} - ${transactionData?.requested_name}.`
                    : `You are about to initiate an E Top Up request from ${
                      headerContent
                        ? `${transactionData?.requested_user_code} - ${transactionData?.requested_name}`
                        : `${formData?.request_from?.user_code} - ${formData?.request_from?.user_name || formData?.request_from?.name || `${formData?.request_from?.first_name ?? ""} ${formData?.request_from?.last_name ?? ""}`.trim()}`
                    }.`}`
                )
              : translate(
                  `${headerContent && defaultPaymentType === "paid"
                    ? `You are about to repay the advance taken from ${transactionData?.requested_user_code} - ${transactionData?.requested_name} for AFA ${Number(formData?.amount).toFixed(2)}.`
                    : `You are about to initiate an E Top Up request from ${
                      headerContent
                        ? `${transactionData?.requested_user_code} - ${transactionData?.requested_name}`
                        : `${formData?.request_from?.user_code} - ${formData?.request_from?.user_name || formData?.request_from?.name || `${formData?.request_from?.first_name ?? ""} ${formData?.request_from?.last_name ?? ""}`.trim()}`
                    } for AFA ${Number(formData?.amount).toFixed(2)}.`}`
                )
          }
          
          onHandleClose={() =>
            defaultPaymentType === "stock_transfer" ? setIsPopupOpen(false) : isPopupPage === 2 ? setIsOpenPage(1) : setIsPopupOpen(false)
          }
          primaryButtonText={
            defaultPaymentType === "stock_transfer" ? translate("Confirm") : isPopupPage === 1 ? translate("Next") : translate("Confirm")
          }
          secondaryButtonText={
            defaultPaymentType === "stock_transfer" ? translate("Cancel") : isPopupPage === 1 ? translate("Cancel") : translate("Back")
          }
          handelSubmit={handleSubmitPopup}
          buttonColor={"bg-p_primary"}
          buttonLoading={popUpLoading}
          buttonWidth="w-[108px]"
          reverseButton="justify-end"
        />
      )}

      {/* PIN verification modal */}
      {isVerificationModelOpen && (
        <PinVerificationModel
          handleClose={() => setIsVerificationModelOpen(false)}
          pin={pin}
          setPin={setPin}
          handleDropdownChange={handleInputChange}
          selectedDropDownValue={formData?.pin_interval}
          isDontAskAgainChecked={formData?.isDontAskAgainChecked}
          handleClickVerify={() => handleClickVerify(true)}
          verifyLoading={verifyLoading}
          errorMessage={errorMessage}
          setErrorMessage={setErrorMessage}
          dropdownError={errors?.pin_interval}
        />
      )}

      {/* Transaction receipt view */}
      {isTransactionReceipt && (
        <TransactionReceipt
          details={responseData}
          handleClickDone={handleClickDone}
          defaultPaymentType={defaultPaymentType}
          {...(defaultPaymentType === "stock_transfer"
            ? { headerMessage: "Stock Transferred Successfully" }
            : headerContent && defaultPaymentType === "paid"
            ? { headerMessage: "E top Up Due Paid Successfully" }
            : {})}
        />
      )}
    </div>
  );
}
